/**
 * SDP exchange for a dictation WebRTC session (POST /dictation/connect).
 *
 * The api-server already retries transient OpenAI failures inside its request
 * deadline, so a 502/504 means those retries are spent and another round would
 * only double the wait. This layer retries what the server never handled: a
 * request dropped at the network layer, or a 503 from the gateway finding no
 * healthy task (e.g. mid api-server rollout).
 */

import { createApiClient } from "~/api/apiClient";
import { ApiNetworkError } from "~/error-handling/exceptions";
import type { DictationSurface } from "~/services/webrtcTranscription";

const CONNECT_ATTEMPTS = 2;
const CONNECT_RETRY_DELAY_MS = 1000;

export class DictationConnectError extends Error {
  constructor(public status: number) {
    super(`Dictation connect failed with HTTP ${status}`);
    this.name = "DictationConnectError";
  }
}

const isRetryable = (error: unknown): boolean =>
  (error instanceof ApiNetworkError && !error.aborted) ||
  (error instanceof DictationConnectError && error.status === 503);

async function exchangeOnce(
  sdp: string,
  surface: DictationSurface,
): Promise<string> {
  const { data, response } = await createApiClient().POST(
    "/dictation/connect",
    { body: { sdp, surface } },
  );
  if (!data) {
    // Non-2xx statuses are reported centrally by the api client's middleware.
    throw new DictationConnectError(response.status);
  }
  return data.sdp;
}

/** Returns OpenAI's SDP answer for the browser's offer. */
export async function exchangeDictationSdp(
  sdp: string,
  surface: DictationSurface,
): Promise<string> {
  for (let attempt = 1; ; attempt++) {
    try {
      return await exchangeOnce(sdp, surface);
    } catch (error) {
      if (attempt >= CONNECT_ATTEMPTS || !isRetryable(error)) throw error;
      await new Promise((resolve) =>
        setTimeout(resolve, CONNECT_RETRY_DELAY_MS),
      );
    }
  }
}
